// Exact secret-free motion authority copied from the newest deployed SeenStage
// desktop source (afd21965; donor SHA-256 64ae4b9eefc2aa1c068f9e5589f9025697eade86079feb1c3edab9d451538d10).
// Business Ops owns its own analyser/session; this file imports no donor state.

export type KaiOrbMotionFrame = Readonly<{
  level: number;
  bass: number;
  mid: number;
  treble: number;
}>;

export type KaiOrbMotionAuthority = Readonly<{
  source: "pcm" | "idle";
  frame: KaiOrbMotionFrame;
}>;

const ZERO_PCM_FRAME: KaiOrbMotionFrame = Object.freeze({
  level: 0,
  bass: 0,
  mid: 0,
  treble: 0,
});

export function selectKaiOrbMotionAuthority(input: {
  analyserPresent: boolean;
  contextState: string | null;
  pcm: KaiOrbMotionFrame;
}): KaiOrbMotionAuthority {
  if (!input.analyserPresent) {
    // WHY 2026-08-04 canonical audible-motion ownership:
    // Transcript timing, React speech state and browser boundary events can lead
    // real output or survive after it. A manufactured fallback made the Orb move
    // before sound, keep moving after sound, and visibly change animation when
    // the output graph was late. No analyser means calm idle. Supported output
    // paths must attach their analyser; a graph failure is surfaced by its owner.
    return {
      source: "idle",
      frame: ZERO_PCM_FRAME,
    };
  }

  // WHY 2026-07-29 PF-17/PF-18 actual-audio authority:
  // Once a real output analyser exists, transcript hashes and boundary pulses
  // must not compete with it—even during silence. Web Audio analysers can keep
  // returning their last nonzero sample after suspension, so a non-running
  // context is explicit PCM zero rather than permission to fall back.
  return {
    source: "pcm",
    frame:
      input.contextState === "running"
        ? input.pcm
        : ZERO_PCM_FRAME,
  };
}
